/**
 * work — o "Trabalho": a leitura de FLUXO sobre documentos que já existem.
 *
 * Um trabalho é o conjunto de documentos de um mesmo serviço: o orçamento, o
 * contrato que nasceu dele, a ordem de serviço, o pagamento e a nota. O app já
 * guardava essas ligações (`contracts.quote_id`, `service_orders.quote_id`,
 * `receipts.quote_id`/`service_order_id`, `nfse_documents.quote_id`,
 * `finance_entries.quote_id`) — o que não existia era a leitura de ETAPA em
 * cima delas.
 *
 * ─── POR QUE DERIVADO, E NÃO UMA COLUNA `current_stage` ─────────────────────
 *
 * A alternativa óbvia seria uma tabela `projects` com a etapa corrente avançada
 * por gatilho. Ela seria uma SEGUNDA fonte de verdade sobre um estado que já é
 * inteiramente dedutível dos documentos — a família de bugs do #33/#79/#89, em
 * que a estrutura aceita a escrita e um caminho esquecido faz a tela mentir sem
 * erro nenhum. Pior: o contrato é assinado pelo CLIENTE, numa página pública,
 * por uma edge function `service_role`; avançar uma coluna dali significaria
 * acoplar a assinatura ao estado do trabalho, mais os executores offline, mais
 * os caminhos de update de cada tabela. Um deles esquece e o trabalho fica
 * parado numa etapa que já passou.
 *
 * Derivando, "avançar de etapa" não é uma operação: é o recálculo da mesma
 * evidência. Não há o que dessincronizar, e o resultado é retroativo — todo
 * histórico já existente vira trabalho no dia em que a tela sobe.
 *
 * ─── NENHUMA ETAPA É OBRIGATÓRIA (e nenhuma é "pulada") ─────────────────────
 *
 * Um MEI do plano grátis sem CNPJ nunca vai usar contrato (PRO) nem NFS-e (PRO
 * + onboarding fiscal). Se a régua fosse "% de 5 etapas", o fluxo perfeito dele
 * (orçamento → OS → pagamento) marcaria 60% para sempre — o app afirmando que o
 * trabalho está incompleto. Por isso aqui não existe porcentagem nem
 * denominador: cada etapa tem estado PRÓPRIO e uma etapa que não aconteceu é
 * `unused`, não "pendente". A fase do trabalho (`WorkPhase`) é o título honesto,
 * por nome, e ela alcança `paid` sem passar por contrato nem por nota.
 *
 * ─── O QUE ESTE MÓDULO NÃO RESOLVE ──────────────────────────────────────────
 *
 * A corrente tem raiz única. Dois orçamentos para o mesmo serviço (o cliente
 * recusou a v1) são dois trabalhos, e um contrato avulso não alcança a OS —
 * `service_orders` não tem `contract_id`. Isso é limite conhecido da derivação,
 * não descuido: resolver exige uma tabela de agrupamento explícita, que é uma
 * decisão separada e posterior.
 *
 * Tudo aqui é função pura sobre linhas já carregadas (mesmo padrão de
 * `clientSummary.ts` e `paymentsReport.ts`): testável sem renderizar nada e sem
 * tocar em Supabase.
 */
import { computeFinanceStatus, todayIsoDate } from '@/lib/financeStatus';
import { isSaleDocument } from '@/lib/quickSale';

// ===========================================================================
// Vocabulário
// ===========================================================================

export type WorkStageId = 'quote' | 'contract' | 'service_order' | 'payment' | 'nfse';

/**
 * `done`   — a etapa está provada e concluída.
 * `current`— existe documento, mas ele ainda não chegou ao estado terminal bom.
 * `unused` — não existe documento nenhum desta etapa neste trabalho.
 *
 * Não existe estado "pendente/obrigatória": ver o cabeçalho.
 */
export type WorkStageState = 'done' | 'current' | 'unused';

export type WorkPhase = 'quoting' | 'closed' | 'executing' | 'delivered' | 'paid' | 'lost';

/** Ordem canônica do fluxo comercial. É sugestão de leitura, não obrigação. */
export const WORK_STAGE_ORDER: WorkStageId[] = [
  'quote',
  'contract',
  'service_order',
  'payment',
  'nfse',
];

export const WORK_STAGE_LABELS: Record<WorkStageId, string> = {
  quote: 'Orçamento',
  contract: 'Contrato',
  service_order: 'Ordem de serviço',
  payment: 'Pagamento',
  nfse: 'Nota fiscal',
};

/**
 * Rótulos da trilha. Cinco colunas num aparelho de 360px dão ~62px cada, e
 * "Ordem de serviço" por extenso não cabe (armadilhas #51/#52: rótulo longo ao
 * lado de espaço curto não quebra linha graciosamente, ele come o vizinho).
 */
export const WORK_STAGE_SHORT_LABELS: Record<WorkStageId, string> = {
  quote: 'Orçamento',
  contract: 'Contrato',
  service_order: 'OS',
  payment: 'Pagamento',
  nfse: 'Nota',
};

export const WORK_PHASE_LABELS: Record<WorkPhase, string> = {
  quoting: 'Aguardando resposta',
  closed: 'Negócio fechado',
  executing: 'Em execução',
  delivered: 'Concluído',
  paid: 'Pago',
  lost: 'Perdido',
};

// ===========================================================================
// Tipos
// ===========================================================================

/**
 * De qual TABELA o documento veio. Não é o mesmo que a etapa: a etapa de
 * pagamento aceita recibo E lançamento financeiro, e a ação dela é gerar
 * RECIBO — sem distinguir a origem, um lançamento fazia o botão dizer "Gerar
 * outro recibo" sem existir recibo nenhum (visto em device).
 */
export type WorkDocumentKind =
  | 'quote'
  | 'contract'
  | 'service_order'
  | 'receipt'
  | 'finance'
  | 'nfse';

/** Um documento que prova (ou está provando) uma etapa. */
export interface WorkDocumentRef {
  id: string;
  kind: WorkDocumentKind;
  label: string;
  /** Rota interna para abrir o documento. Ver armadilha #56: rotas são bilíngues. */
  route: string;
  /** Status cru da linha de origem, para a tela rotular. */
  status: string;
  /** Documento cancelado/rejeitado — a tela mostra riscado, não some. */
  cancelled: boolean;
  /**
   * Número do documento (`#12`), quando a tabela tem `sequence_id`.
   *
   * Existe porque dois documentos da MESMA etapa podem ter rótulo idêntico —
   * visto em dado real: um recibo gerado pelo orçamento e outro pela OS do mesmo
   * trabalho, ambos com a descrição "Reforma 02" e o mesmo valor, viraram duas
   * linhas indistinguíveis. Sem o número não há como saber qual é qual, e nada
   * falha: só a tela fica indecifrável (irmão do #30d).
   */
  hint: string | null;
}

/** `#12` quando a linha tem número; `null` quando a tabela não numera. */
function documentHint(row: any): string | null {
  return row?.sequence_id ? `#${row.sequence_id}` : null;
}

export interface WorkStage {
  id: WorkStageId;
  state: WorkStageState;
  label: string;
  shortLabel: string;
  /** Texto curto do que está acontecendo ('Aprovado', 'Aguardando assinatura'). */
  detail: string | null;
  documents: WorkDocumentRef[];
}

export interface WorkNextStep {
  stage: WorkStageId;
  /** `finish` = terminar o que já começou; `start` = criar o documento da etapa. */
  kind: 'finish' | 'start';
  label: string;
}

export interface Work {
  title: string;
  clientName: string | null;
  /** Valor de referência do trabalho: o do orçamento raiz, senão o do 1º documento. */
  total: number;
  phase: WorkPhase;
  phaseLabel: string;
  stages: WorkStage[];
  /** `null` quando não há sugestão sensata (trabalho pago ou perdido). */
  nextStep: WorkNextStep | null;
  /**
   * `true` quando o usuário não pode ler `finance_entries` (RLS owner/admin-only)
   * — o pagamento então só pode ser provado por recibo, e a tela precisa dizer
   * isso em vez de afirmar que não houve pagamento. Ver armadilha #38.
   */
  paymentEvidenceLimited: boolean;
}

/**
 * As linhas de UM trabalho, já filtradas por quem carrega (ver `useWork`).
 *
 * `financeEntries: null` significa "este usuário não pode ler o financeiro" —
 * distinto de `[]`, que é "pode ler e não há nada". A distinção é o que separa
 * "não recebi" de "não sei se recebi".
 */
export interface WorkSources {
  /**
   * O orçamento raiz, quando o trabalho tem um. Atalho para `quotes: [x]` —
   * mantido porque a esmagadora maioria dos trabalhos tem exatamente um, e
   * porque um trabalho agrupado é a exceção, não a regra.
   */
  quote?: any | null;
  /** Vários orçamentos, quando o trabalho foi agrupado explicitamente. */
  quotes?: any[];
  contracts?: any[];
  serviceOrders?: any[];
  receipts?: any[];
  nfseDocuments?: any[];
  financeEntries?: any[] | null;
}

export interface WorkOptions {
  /**
   * Etapas que podem ser SUGERIDAS como próximo passo. A trilha sempre mostra as
   * cinco (é a superfície que torna contrato e nota visíveis para quem não
   * assina); o que o plano restringe é apenas o convite. Quem conhece o plano é
   * o hook — este módulo continua sem saber o que é PRO.
   */
  suggestable?: WorkStageId[];
  /** 'YYYY-MM-DD' injetável para o teste não depender do relógio. */
  today?: string;
}

// ===========================================================================
// Raiz e seleção da corrente
// ===========================================================================

export type WorkRootKind = 'quote' | 'contract' | 'service_order' | 'receipt';

/** O documento que ancora o trabalho. Toda seleção parte dele. */
export interface WorkRoot {
  kind: WorkRootKind;
  id: string;
}

/** A linha de onde o usuário está olhando o trabalho. */
export interface WorkAnchor {
  kind: WorkRootKind;
  id: string;
  /** `quote_id` da própria linha (contrato, OS e recibo têm a coluna). */
  quoteId?: string | null;
  /** `service_order_id` — só o recibo tem. */
  serviceOrderId?: string | null;
}

/**
 * Qual documento é a raiz do trabalho visto a partir de `anchor`.
 *
 * O orçamento é a raiz sempre que existe, porque é dele que todo o resto pende.
 * Um recibo gerado a partir de uma OS carrega SÓ `service_order_id` (ver o
 * prefill em `ServiceOrderDetailPage`), então ele precisa de um segundo salto: o
 * `quote_id` daquela OS, que o chamador passa em `serviceOrderQuoteId` quando
 * conseguiu carregá-la. Sem esse salto o recibo viraria um trabalho solto e o
 * usuário veria dois trabalhos onde há um.
 */
export function resolveWorkRoot(
  anchor: WorkAnchor,
  serviceOrderQuoteId?: string | null,
): WorkRoot {
  if (anchor.kind === 'quote') return { kind: 'quote', id: anchor.id };
  if (anchor.quoteId) return { kind: 'quote', id: String(anchor.quoteId) };
  if (anchor.kind === 'receipt' && anchor.serviceOrderId) {
    if (serviceOrderQuoteId) return { kind: 'quote', id: String(serviceOrderQuoteId) };
    return { kind: 'service_order', id: String(anchor.serviceOrderId) };
  }
  return { kind: anchor.kind, id: anchor.id };
}

/** Todas as linhas disponíveis (do servidor ou do cache), antes da seleção. */
export interface WorkRowPool {
  quotes?: any[];
  contracts?: any[];
  serviceOrders?: any[];
  receipts?: any[];
  nfseDocuments?: any[];
  /** `null` = gate de papel; atravessa a seleção intacto. */
  financeEntries?: any[] | null;
}

/**
 * Recorta, do conjunto de linhas, as que pertencem a este trabalho.
 *
 * Esta é a ÚNICA definição de "o que pertence ao trabalho". O caminho online
 * busca com queries direcionadas (por eficiência) e passa o resultado por aqui
 * assim mesmo; o caminho offline passa o cache inteiro. Com uma segunda
 * implementação para o servidor, cache e rede discordariam com o tempo sobre a
 * composição do trabalho, sem erro nenhum na tela (armadilha #93).
 *
 * O salto OS→recibo é o motivo de a seleção ser em dois tempos: as OSs saem do
 * orçamento, e os recibos saem do orçamento OU de qualquer uma dessas OSs.
 */
export function collectWorkSources(root: WorkRoot, pool: WorkRowPool): WorkSources {
  const quotes = pool.quotes ?? [];
  const contracts = pool.contracts ?? [];
  const serviceOrders = pool.serviceOrders ?? [];
  const receipts = pool.receipts ?? [];
  const nfseDocuments = pool.nfseDocuments ?? [];
  const financeEntries = pool.financeEntries;
  const sameId = (value: unknown, id: string) => value != null && String(value) === id;

  if (root.kind === 'quote') {
    const ownOrders = serviceOrders.filter((o) => sameId(o?.quote_id, root.id));
    const orderIds = new Set(ownOrders.map((o) => String(o.id)));
    return {
      quote: quotes.find((q) => sameId(q?.id, root.id)) ?? null,
      contracts: contracts.filter((c) => sameId(c?.quote_id, root.id)),
      serviceOrders: ownOrders,
      receipts: receipts.filter(
        (r) =>
          sameId(r?.quote_id, root.id) ||
          (r?.service_order_id != null && orderIds.has(String(r.service_order_id))),
      ),
      nfseDocuments: nfseDocuments.filter((n) => sameId(n?.quote_id, root.id)),
      financeEntries:
        financeEntries === null || financeEntries === undefined
          ? (financeEntries as any)
          : financeEntries.filter((e) => sameId(e?.quote_id, root.id)),
    };
  }

  if (root.kind === 'service_order') {
    // OS avulsa. `contracts` não tem `contract_id` do lado da OS e nem
    // `finance_entries` nem `nfse_documents` têm `service_order_id` — não há
    // corrente a seguir, e inventar uma seria pior que mostrar o que existe.
    return {
      quote: null,
      contracts: [],
      serviceOrders: serviceOrders.filter((o) => sameId(o?.id, root.id)),
      receipts: receipts.filter((r) => sameId(r?.service_order_id, root.id)),
      nfseDocuments: [],
      financeEntries: financeEntries === null ? null : [],
    };
  }

  if (root.kind === 'contract') {
    return {
      quote: null,
      contracts: contracts.filter((c) => sameId(c?.id, root.id)),
      serviceOrders: [],
      receipts: [],
      nfseDocuments: [],
      financeEntries: financeEntries === null ? null : [],
    };
  }

  return {
    quote: null,
    contracts: [],
    serviceOrders: [],
    receipts: receipts.filter((r) => sameId(r?.id, root.id)),
    nfseDocuments: [],
    financeEntries: financeEntries === null ? null : [],
  };
}

/**
 * Une as correntes de várias raízes num trabalho só (agrupamento explícito).
 *
 * Deduplica por id porque as correntes se sobrepõem de propósito: dois
 * orçamentos agrupados podem ter gerado a MESMA OS, e contá-la duas vezes faria
 * a etapa listar o documento em dobro.
 *
 * `financeEntries` propaga o `null` de qualquer parte: se uma das correntes veio
 * sem permissão de ler o financeiro, o conjunto inteiro está incompleto, e dizer
 * "não recebido" seria pior do que dizer "não sei" (#38).
 */
export function mergeWorkSources(parts: WorkSources[]): WorkSources {
  const dedupe = (rows: any[]): any[] => {
    const seen = new Set<string>();
    return rows.filter((row) => {
      const id = String(row?.id ?? '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  };
  const collect = (pick: (p: WorkSources) => any[] | undefined): any[] =>
    dedupe(parts.flatMap((p) => pick(p) ?? []));

  const financeBlocked = parts.some((p) => p.financeEntries === null);

  return {
    quotes: dedupe(parts.flatMap((p) => p.quotes ?? (p.quote ? [p.quote] : []))),
    contracts: collect((p) => p.contracts),
    serviceOrders: collect((p) => p.serviceOrders),
    receipts: collect((p) => p.receipts),
    nfseDocuments: collect((p) => p.nfseDocuments),
    financeEntries: financeBlocked ? null : collect((p) => p.financeEntries ?? []),
  };
}

// ===========================================================================
// Helpers
// ===========================================================================

function money(value: unknown): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function firstNonEmpty(...candidates: unknown[]): string {
  for (const candidate of candidates) {
    const text = typeof candidate === 'string' ? candidate.trim() : '';
    if (text) return text;
  }
  return '';
}

const isCancelledStatus = (status: unknown): boolean =>
  status === 'cancelled' || status === 'rejected';

// ===========================================================================
// Etapas
// ===========================================================================

/**
 * Etapa de orçamento — aceita VÁRIOS, porque um trabalho agrupado pode ter mais
 * de um (o cliente recusou a v1 e aprovou a v2). Basta UM aprovado para a etapa
 * fechar: o serviço foi contratado, e a proposta recusada faz parte da história
 * sem impedir nada.
 */
function buildQuoteStage(quotes: any[]): WorkStage {
  const base = {
    id: 'quote' as const,
    label: WORK_STAGE_LABELS.quote,
    shortLabel: WORK_STAGE_SHORT_LABELS.quote,
  };

  const documents: WorkDocumentRef[] = quotes
    .filter((q) => q?.id)
    .map((q) => {
      const status = String(q.status ?? 'pending');
      return {
        id: String(q.id),
        kind: 'quote' as const,
        label: firstNonEmpty(q.title, 'Orçamento'),
        route: `/quotes/${q.id}`,
        status,
        cancelled: isCancelledStatus(status),
        hint: documentHint(q),
      };
    });

  if (documents.length === 0) {
    // OS ou contrato avulso: o trabalho existe sem nunca ter passado por
    // orçamento, e isso é normal — não é uma etapa "faltando".
    return { ...base, state: 'unused', detail: null, documents: [] };
  }

  const approved = documents.find((d) => d.status === 'approved' || d.status === 'completed');
  if (approved) {
    return {
      ...base,
      state: 'done',
      detail: approved.status === 'completed' ? 'Concluído' : 'Aprovado',
      documents,
    };
  }
  if (documents.some((d) => d.status === 'pending')) {
    return { ...base, state: 'current', detail: 'Aguardando aprovação', documents };
  }
  if (documents.some((d) => d.status === 'rejected')) {
    return { ...base, state: 'current', detail: 'Recusado', documents };
  }
  return { ...base, state: 'current', detail: 'Cancelado', documents };
}

function buildContractStage(contracts: any[]): WorkStage {
  const base = {
    id: 'contract' as const,
    label: WORK_STAGE_LABELS.contract,
    shortLabel: WORK_STAGE_SHORT_LABELS.contract,
  };

  const documents: WorkDocumentRef[] = contracts
    .filter((c) => c?.id)
    .map((c) => ({
      id: String(c.id),
      kind: 'contract',
      label: firstNonEmpty(
        c.title,
        c.sequence_id ? `Contrato #${c.sequence_id}` : '',
        'Contrato',
      ),
      route: `/contratos/${c.id}`,
      status: String(c.status ?? 'draft'),
      cancelled: isCancelledStatus(c.status),
      hint: documentHint(c),
    }));

  if (documents.length === 0) {
    return { ...base, state: 'unused', detail: null, documents: [] };
  }
  const live = documents.filter((d) => !d.cancelled);
  if (live.length === 0) {
    return { ...base, state: 'current', detail: 'Cancelado', documents };
  }
  if (live.some((d) => d.status === 'signed')) {
    return { ...base, state: 'done', detail: 'Assinado', documents };
  }
  if (live.some((d) => d.status === 'partially_signed')) {
    return { ...base, state: 'current', detail: 'Falta uma assinatura', documents };
  }
  if (live.some((d) => d.status === 'sent')) {
    return { ...base, state: 'current', detail: 'Enviado ao cliente', documents };
  }
  return { ...base, state: 'current', detail: 'Rascunho', documents };
}

function buildServiceOrderStage(serviceOrders: any[]): WorkStage {
  const base = {
    id: 'service_order' as const,
    label: WORK_STAGE_LABELS.service_order,
    shortLabel: WORK_STAGE_SHORT_LABELS.service_order,
  };

  const documents: WorkDocumentRef[] = serviceOrders
    .filter((o) => o?.id)
    .map((o) => ({
      id: String(o.id),
      kind: 'service_order',
      label: firstNonEmpty(o.title, o.sequence_id ? `OS #${o.sequence_id}` : '', 'Ordem de serviço'),
      route: `/ordens-servico/${o.id}`,
      status: String(o.status ?? 'pending'),
      cancelled: isCancelledStatus(o.status),
      hint: documentHint(o),
    }));

  if (documents.length === 0) {
    return { ...base, state: 'unused', detail: null, documents: [] };
  }
  const live = documents.filter((d) => !d.cancelled);
  if (live.length === 0) {
    return { ...base, state: 'current', detail: 'Cancelada', documents };
  }
  // Com várias OSs no mesmo trabalho, a etapa só fecha quando TODAS as vivas
  // fecharam — senão uma OS concluída esconderia outra ainda em campo.
  if (live.every((d) => d.status === 'completed')) {
    return { ...base, state: 'done', detail: 'Concluída', documents };
  }
  if (live.some((d) => d.status === 'in_progress')) {
    return { ...base, state: 'current', detail: 'Em andamento', documents };
  }
  return { ...base, state: 'current', detail: 'A executar', documents };
}

/**
 * Pagamento — provado pelo FINANCEIRO ou por recibo.
 *
 * Recibo sozinho não bastaria: muita gente dá baixa no lançamento e nunca emite
 * recibo, e o trabalho ficaria eternamente "não pago" com o dinheiro na conta.
 *
 * Só entra `kind === 'income'`. Uma DESPESA COBRÁVEL (#76) também carrega
 * `quote_id`, e somá-la aqui faria um gasto do prestador passar por recebimento.
 */
function buildPaymentStage(
  receipts: any[],
  financeEntries: any[] | null | undefined,
  today: string,
): WorkStage {
  const base = {
    id: 'payment' as const,
    label: WORK_STAGE_LABELS.payment,
    shortLabel: WORK_STAGE_SHORT_LABELS.payment,
  };

  const receiptDocs: WorkDocumentRef[] = receipts
    .filter((r) => r?.id)
    .map((r) => ({
      id: String(r.id),
      kind: 'receipt',
      label: firstNonEmpty(
        r.description,
        r.sequence_id ? `Recibo #${r.sequence_id}` : '',
        'Recibo',
      ),
      route: `/recibos/${r.id}`,
      // `receipts` não tem coluna `status` (#57): o cancelamento é o carimbo.
      status: r.cancelled_at ? 'cancelled' : 'issued',
      cancelled: Boolean(r.cancelled_at),
      hint: documentHint(r),
    }));

  const income = (financeEntries ?? []).filter((e) => e?.id && e.kind === 'income');
  const financeDocs: WorkDocumentRef[] = income.map((e) => {
    const status = e.computed_status ?? computeFinanceStatus(e.status, e.due_date, today);
    return {
      id: String(e.id),
      kind: 'finance',
      label: firstNonEmpty(e.description, 'Lançamento'),
      // O financeiro não tem rota de detalhe por id — abre o módulo.
      route: '/financeiro',
      status: String(status),
      cancelled: status === 'canceled',
      // `finance_entries` não tem numeração de documento.
      hint: null,
    };
  });

  const documents = [...financeDocs, ...receiptDocs];
  if (documents.length === 0) {
    return { ...base, state: 'unused', detail: null, documents: [] };
  }

  const paidEntries = financeDocs.filter((d) => d.status === 'paid');
  const openEntries = financeDocs.filter((d) => d.status === 'pending' || d.status === 'overdue');
  const liveReceipts = receiptDocs.filter((d) => !d.cancelled);

  // Um lançamento ainda em aberto vence o recibo: o recibo pode ser de um sinal.
  if (openEntries.length > 0) {
    const overdue = openEntries.some((d) => d.status === 'overdue');
    return {
      ...base,
      state: 'current',
      detail: overdue ? 'Em atraso' : 'A receber',
      documents,
    };
  }
  if (paidEntries.length > 0 || liveReceipts.length > 0) {
    return { ...base, state: 'done', detail: 'Recebido', documents };
  }
  // Só sobrou lançamento cancelado / recibo cancelado.
  return { ...base, state: 'current', detail: 'Cancelado', documents };
}

function buildNfseStage(nfseDocuments: any[]): WorkStage {
  const base = {
    id: 'nfse' as const,
    label: WORK_STAGE_LABELS.nfse,
    shortLabel: WORK_STAGE_SHORT_LABELS.nfse,
  };

  const documents: WorkDocumentRef[] = nfseDocuments
    .filter((n) => n?.id)
    .map((n) => ({
      id: String(n.id),
      kind: 'nfse',
      label: n.numero ? `NFS-e ${n.numero}` : 'Nota fiscal de serviço',
      // O módulo fiscal abre a nota por diálogo, não por rota de detalhe.
      route: '/nfse',
      status: String(n.status ?? 'draft'),
      cancelled: n.status === 'cancelled',
      // A nota já leva o próprio número no rótulo.
      hint: null,
    }));

  if (documents.length === 0) {
    return { ...base, state: 'unused', detail: null, documents: [] };
  }
  const live = documents.filter((d) => !d.cancelled);
  if (live.length === 0) {
    return { ...base, state: 'current', detail: 'Cancelada', documents };
  }
  if (live.some((d) => d.status === 'authorized')) {
    return { ...base, state: 'done', detail: 'Autorizada', documents };
  }
  if (live.some((d) => d.status === 'rejected')) {
    return { ...base, state: 'current', detail: 'Rejeitada', documents };
  }
  return { ...base, state: 'current', detail: 'Em processamento', documents };
}

// ===========================================================================
// Fase e próximo passo
// ===========================================================================

function stageById(stages: WorkStage[], id: WorkStageId): WorkStage {
  return stages.find((s) => s.id === id)!;
}

/**
 * A fase é o título honesto do trabalho. A ordem de avaliação importa: um
 * trabalho pago não é "perdido" mesmo que o orçamento tenha sido cancelado
 * depois, e um trabalho perdido não deve anunciar "em execução".
 */
function derivePhase(stages: WorkStage[], quotes: any[]): WorkPhase {
  const payment = stageById(stages, 'payment');
  const serviceOrder = stageById(stages, 'service_order');
  const contract = stageById(stages, 'contract');
  const quoteStage = stageById(stages, 'quote');

  if (payment.state === 'done') return 'paid';
  // Num trabalho agrupado, perdido é quando TODAS as propostas caíram — uma v1
  // recusada com a v2 aprovada não é um trabalho perdido, é o caminho normal.
  if (quotes.length > 0 && quotes.every((q) => isCancelledStatus(q?.status))) return 'lost';
  if (serviceOrder.state === 'done' || quotes.some((q) => q?.status === 'completed')) {
    return 'delivered';
  }
  if (serviceOrder.state === 'current' && serviceOrder.detail !== 'Cancelada') return 'executing';
  if (quoteStage.state === 'done' || contract.state === 'done') return 'closed';
  return 'quoting';
}

const FINISH_LABELS: Record<WorkStageId, string> = {
  quote: 'Aprovar o orçamento',
  contract: 'Concluir a assinatura',
  service_order: 'Concluir a OS',
  payment: 'Dar baixa no pagamento',
  nfse: 'Acompanhar a nota',
};

/**
 * Rótulo do BOTÃO de cada etapa, em duas formas.
 *
 * A forma `repeat` é o que remove a contradição da tela antiga: o card de ações
 * era stateless e convidava a "Gerar recibo" num trabalho que já tinha dois. A
 * saída não é esconder a ação — recibo de sinal e recibo de saldo são dois
 * recibos legítimos, e um orçamento grande vira duas OSs —, é o app RECONHECER o
 * que já existe. "Gerar outro recibo" informa e continua permitindo.
 *
 * Ficam aqui, e não nas páginas, porque as três telas oferecem as mesmas ações:
 * o texto duplicado divergiria na primeira vez que alguém mexesse num só lugar.
 */
export const WORK_ACTION_LABELS: Record<WorkStageId, { start: string; repeat: string }> = {
  quote: { start: 'Criar orçamento', repeat: 'Criar outro orçamento' },
  contract: { start: 'Gerar contrato', repeat: 'Gerar outro contrato' },
  service_order: { start: 'Gerar ordem de serviço', repeat: 'Gerar outra ordem de serviço' },
  payment: { start: 'Gerar recibo', repeat: 'Gerar outro recibo' },
  nfse: { start: 'Emitir NFS-e', repeat: 'Emitir outra NFS-e' },
};

/**
 * A tabela que CADA AÇÃO cria. Só ela conta para o "outro".
 *
 * A etapa de pagamento é a razão de isto existir: ela é provada por recibo OU
 * por lançamento financeiro, mas a ação é "Gerar recibo". Contando qualquer
 * documento, registrar um recebimento no financeiro fazia o botão oferecer
 * "Gerar OUTRO recibo" sem existir recibo nenhum — achado em device real.
 */
const ACTION_DOCUMENT_KIND: Record<WorkStageId, WorkDocumentKind> = {
  quote: 'quote',
  contract: 'contract',
  service_order: 'service_order',
  payment: 'receipt',
  nfse: 'nfse',
};

/**
 * O rótulo certo para a etapa no estado em que ela está. "Já existe um
 * documento DESTE tipo" é a pergunta — não "a etapa está concluída": um recibo
 * cancelado ou um contrato em rascunho também já ocupam a etapa.
 */
export function workActionLabel(stage: WorkStage): string {
  const labels = WORK_ACTION_LABELS[stage.id];
  const kind = ACTION_DOCUMENT_KIND[stage.id];
  const existing = stage.documents.filter((doc) => doc.kind === kind).length;
  return existing > 0 ? labels.repeat : labels.start;
}

const START_LABELS: Record<WorkStageId, string> = {
  quote: 'Criar o orçamento',
  contract: 'Gerar contrato',
  service_order: 'Gerar ordem de serviço',
  payment: 'Registrar o pagamento',
  nfse: 'Emitir NFS-e',
};

/**
 * Sugestão única de próximo passo. Terminar o que já começou vem antes de
 * começar algo novo — um trabalho com a OS em campo não deve convidar a emitir
 * nota.
 */
function deriveNextStep(
  stages: WorkStage[],
  phase: WorkPhase,
  suggestable: WorkStageId[],
): WorkNextStep | null {
  // Nada a sugerir: o dinheiro entrou, ou o cliente disse não.
  if (phase === 'paid' || phase === 'lost') return null;

  for (const id of WORK_STAGE_ORDER) {
    const stage = stageById(stages, id);
    if (stage.state === 'current' && !stage.detail?.startsWith('Cancel')) {
      return { stage: id, kind: 'finish', label: FINISH_LABELS[id] };
    }
  }
  for (const id of WORK_STAGE_ORDER) {
    const stage = stageById(stages, id);
    if (stage.state === 'unused' && suggestable.includes(id)) {
      return { stage: id, kind: 'start', label: START_LABELS[id] };
    }
  }
  return null;
}

// ===========================================================================
// Montagem
// ===========================================================================

// ===========================================================================
// Lista de trabalhos
// ===========================================================================

/** Fases em que o trabalho ainda pede alguma coisa do prestador. */
export const WORK_OPEN_PHASES: WorkPhase[] = ['quoting', 'closed', 'executing', 'delivered'];

/** Em aberto = ainda não recebeu e não foi perdido. */
export function isOpenWork(work: Work): boolean {
  return work.phase !== 'paid' && work.phase !== 'lost';
}

/** Forma mínima de um vínculo de agrupamento (espelha `work_links`). */
export interface WorkGroupLink {
  work_key: string;
  document_kind: WorkRootKind;
  document_id: string;
}

export interface WorkListEntry {
  /** A raiz que representa o trabalho na lista (a "cabeça" do grupo). */
  root: WorkRoot;
  work: Work;
  /** Data do documento raiz — a ordem da lista. `null` quando ausente/inválida. */
  sortDate: Date | null;
}

/**
 * Todos os trabalhos que o conjunto de linhas contém.
 *
 * ─── QUEM É RAIZ ────────────────────────────────────────────────────────────
 *
 * Orçamento (menos PDV), OS avulsa e contrato avulso. São os três documentos
 * que representam um compromisso comercial e dos quais outros documentos podem
 * pender.
 *
 * **Recibo NUNCA é raiz** — e não é uma escolha de gosto: um recibo é sempre
 * FOLHA da corrente (nenhuma tabela tem `receipt_id`), então um recibo avulso
 * seria eternamente um trabalho de um documento só, já nascido "Pago". Tratá-lo
 * como trabalho encheria a lista de comprovantes de serviços que o app nunca
 * acompanhou, empurrando para baixo o que de fato está em aberto.
 *
 * Montar em memória (e não uma consulta por raiz) é o que evita o N+1: a
 * seleção de cada trabalho sai de `collectWorkSources`, a mesma função do
 * detalhe — a lista e a tela do documento não podem discordar sobre a fase.
 */
export function buildWorkList(
  pool: WorkRowPool,
  options: WorkOptions = {},
  /**
   * Vínculos de agrupamento explícito. Sem eles a lista mostraria como DOIS
   * trabalhos o que o detalhe mostra como um — divergência medida em device: a
   * OS avulsa e o orçamento agrupados apareciam separados em `/trabalhos`.
   */
  links: WorkGroupLink[] = [],
): WorkListEntry[] {
  const roots: WorkRoot[] = [];

  for (const quote of pool.quotes ?? []) {
    // Nada de filtrar PDV aqui: `buildWork` já devolve `null` para venda de
    // balcão, e a entrada é descartada logo abaixo. Repetir o predicado daria
    // DUAS definições de "isto é uma venda?" — que é exatamente o que a
    // armadilha #82 proíbe, porque a segunda envelhece em silêncio.
    if (!quote?.id) continue;
    roots.push({ kind: 'quote', id: String(quote.id) });
  }
  for (const order of pool.serviceOrders ?? []) {
    if (order?.id && !order.quote_id) roots.push({ kind: 'service_order', id: String(order.id) });
  }
  for (const contract of pool.contracts ?? []) {
    if (contract?.id && !contract.quote_id) {
      roots.push({ kind: 'contract', id: String(contract.id) });
    }
  }

  const dateOf = (row: any): Date | null => {
    if (!row?.created_at) return null;
    const date = new Date(String(row.created_at));
    return Number.isNaN(date.getTime()) ? null : date;
  };
  const rowFor = (root: WorkRoot): any => {
    const list =
      root.kind === 'quote'
        ? pool.quotes
        : root.kind === 'service_order'
          ? pool.serviceOrders
          : pool.contracts;
    return (list ?? []).find((r: any) => String(r?.id) === root.id) ?? null;
  };

  /**
   * Junta as raízes agrupadas ANTES de montar: um grupo vira UMA entrada. Sem
   * isto a lista mostra dois trabalhos onde o detalhe mostra um — e as duas
   * telas passam a discordar sobre quantos serviços existem.
   *
   * Raiz sem vínculo recebe uma chave própria (o próprio identificador), então o
   * caminho não agrupado continua sendo exatamente o de antes.
   */
  const keyOf = (root: WorkRoot): string => {
    const link = links.find(
      (l) => l.document_kind === root.kind && l.document_id === root.id,
    );
    return link ? `k:${link.work_key}` : `r:${root.kind}:${root.id}`;
  };
  const groups = new Map<string, WorkRoot[]>();
  for (const root of roots) {
    const key = keyOf(root);
    const list = groups.get(key);
    if (list) list.push(root);
    else groups.set(key, [root]);
  }

  const entries: WorkListEntry[] = [];
  for (const groupRoots of groups.values()) {
    // A "cabeça" do grupo é o orçamento quando há um — é dele que saem título e
    // valor, e é a leitura que o usuário reconhece como o trabalho.
    //
    // Hoje isto é redundante (as raízes são empilhadas com os orçamentos
    // primeiro, então `groupRoots[0]` já seria o orçamento) e por isso NENHUMA
    // sabotagem consegue distinguir as duas formas — não há guard aqui, de
    // propósito declarado. Fica explícito mesmo assim para não virar dependência
    // silenciosa da ordem dos `push` acima.
    const head = groupRoots.find((r) => r.kind === 'quote') ?? groupRoots[0];
    const work = buildWork(
      mergeWorkSources(groupRoots.map((r) => collectWorkSources(r, pool))),
      options,
    );
    if (!work) continue;
    // A data é a do documento MAIS ANTIGO do grupo: é quando o trabalho começou.
    const dates = groupRoots.map((r) => dateOf(rowFor(r))).filter(Boolean) as Date[];
    const sortDate = dates.length > 0 ? new Date(Math.min(...dates.map((d) => d.getTime()))) : null;
    entries.push({ root: head, work, sortDate });
  }

  // Mais recente primeiro; desempate por id para a ordem não "piscar" entre
  // revalidações quando dois documentos nascem no mesmo instante.
  entries.sort((a, b) => {
    const diff = (b.sortDate?.getTime() ?? 0) - (a.sortDate?.getTime() ?? 0);
    return diff !== 0 ? diff : a.root.id.localeCompare(b.root.id);
  });
  return entries;
}

/** Agrupa por fase, preservando a ordem interna e omitindo fase vazia. */
export function groupWorksByPhase(
  entries: WorkListEntry[],
): Array<{ phase: WorkPhase; label: string; entries: WorkListEntry[] }> {
  const order: WorkPhase[] = ['executing', 'closed', 'quoting', 'delivered', 'paid', 'lost'];
  return order
    .map((phase) => ({
      phase,
      label: WORK_PHASE_LABELS[phase],
      entries: entries.filter((e) => e.work.phase === phase),
    }))
    .filter((group) => group.entries.length > 0);
}

/**
 * Quantos documentos o trabalho reúne.
 *
 * Serve para a tela decidir se vale mostrar a trilha: um trabalho com UM
 * documento só (recibo avulso, OS avulsa recém-criada) desenharia quatro etapas
 * vazias e diria menos que o link simples de origem que já existe.
 */
export function workDocumentCount(work: Work | null | undefined): number {
  return (work?.stages ?? []).reduce((total, stage) => total + stage.documents.length, 0);
}

/**
 * Monta o trabalho a partir das linhas já carregadas.
 *
 * Devolve `null` quando não há trabalho a mostrar:
 *
 * - **Venda de balcão (PDV).** É um `quotes` com `document_type='pedido'` que já
 *   nasce `completed` — não existe fluxo a acompanhar, a venda acabou no balcão.
 *   O predicado é `isSaleDocument` (fonte única, armadilha #82); recriá-lo aqui
 *   faria esta tela discordar do Dashboard e dos relatórios sobre o que é um
 *   orçamento. E ele é POSITIVO de propósito: `document_type` é nullable, então
 *   testar `!== 'pedido'` trataria todo orçamento legado como venda.
 * - **Nenhum documento.** Sem evidência não há etapa nenhuma para desenhar.
 */
export function buildWork(sources: WorkSources, options: WorkOptions = {}): Work | null {
  // `quote` (singular) é atalho de `quotes: [x]`. Normalizar aqui é o que deixa
  // o resto do módulo com um caminho só.
  const allQuotes = sources.quotes ?? (sources.quote ? [sources.quote] : []);
  // PDV fora. Num grupo, basta uma venda de balcão não fazer parte dele — o
  // filtro é por linha, não pelo conjunto.
  const quotes = allQuotes.filter((q) => q?.id && !isSaleDocument(q));
  if (allQuotes.length > 0 && quotes.length === 0) return null;
  const quote = quotes[0] ?? null;

  const contracts = sources.contracts ?? [];
  const serviceOrders = sources.serviceOrders ?? [];
  const receipts = sources.receipts ?? [];
  const nfseDocuments = sources.nfseDocuments ?? [];
  const financeEntries = sources.financeEntries;
  const today = options.today ?? todayIsoDate();
  const suggestable = options.suggestable ?? [...WORK_STAGE_ORDER];

  const stages: WorkStage[] = [
    buildQuoteStage(quotes),
    buildContractStage(contracts),
    buildServiceOrderStage(serviceOrders),
    buildPaymentStage(receipts, financeEntries, today),
    buildNfseStage(nfseDocuments),
  ];

  if (stages.every((s) => s.state === 'unused')) return null;

  const phase = derivePhase(stages, quotes);

  // Título e cliente saem do orçamento raiz quando ele existe; senão, do
  // primeiro documento que tiver algo — um trabalho sem nome nenhum na tela é
  // indistinguível dos outros na lista.
  const fallbackDoc = [...contracts, ...serviceOrders, ...receipts][0] ?? null;
  const title = firstNonEmpty(
    quote?.title,
    fallbackDoc?.title,
    fallbackDoc?.description,
    quote?.client_name,
    fallbackDoc?.client_name,
    fallbackDoc?.payer_name,
    'Trabalho',
  );
  const rawClientName =
    firstNonEmpty(quote?.client_name, fallbackDoc?.client_name, fallbackDoc?.payer_name) || null;
  // Documento sem título cai no nome do cliente — e aí repetir o nome na linha
  // de baixo produz "Fulano / Fulano · Em orçamento". Só aparece com dado real
  // (todo fixture tem título), como o #52 e o #87.
  const clientName = rawClientName === title ? null : rawClientName;

  return {
    title,
    clientName,
    total: money(quote?.total ?? fallbackDoc?.total ?? fallbackDoc?.amount),
    phase,
    phaseLabel: WORK_PHASE_LABELS[phase],
    stages,
    nextStep: deriveNextStep(stages, phase, suggestable),
    // `undefined` é "o chamador não passou financeiro" (nada a dizer);
    // `null` é o gate de papel, que a tela precisa explicar.
    paymentEvidenceLimited: financeEntries === null,
  };
}
